"use client";

import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { Tabs, TabPanel } from "react-aria-components";
import { useMove } from "react-aria";
import * as m from "motion/react-m";
import { AnimatePresence } from "motion/react";
import clsx from "clsx";
import { useTranslations } from "next-intl";

import { useHasFinePointer } from "@/app/_utilities/useHasFinePointer";

import { Button } from "@ui/components/core/Button";
import { TabList } from "@ui/components/core/TabList";
import { DiscPlayer } from "@ui/components/core/Disc/DiscPlayer";
import { ImageSizes } from "@ui/lib/image";
import { NativeAudioProvider, useNativeAudio } from "@/app/_providers/native-audio";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { appUrls } from "@/constants";
import { EventName } from "@mw/shared/analyticsEventNames";
import { captureEvent } from "@/app/_analytics/analytics";
import { ViewLayout } from "@ui/components/core/SideBySideOmnibox/ViewLayout";
import { OmniboxLayoutWrapper } from "../OmniboxLayoutWrapper";
import { SpeechInner } from "@/app/_blocks/BlockInteractCreateOmnibox/demos/Speech/Speech";
import { VoicesWithTTS, type VoicesWithTTSHandle } from "@ui/components/demos/VoicesWithTTS/VoicesWithTTS";
import { getPreviewUrlForLanguage, type VoiceWithPreview } from "@ui/components/demos/VoicesWithTTS/types";
import { OrbCarouselContent } from "./OrbCarouselContent";
import { OrbCarouselFooter } from "./OrbCarouselFooter";
import { TTSDownloadSignUpPopover } from "./TTSDownloadSignUpPopover";
import IconArrowUpRight from "@ui/central-icons/icons/IconArrowUpRight";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { LocalStorageKeys, safeLocalStorage } from "@utilities/localStorage";
import ChevronLeft24 from "@ui/icons/24x24/chevron-left-24.svg";
import ChevronRight24 from "@ui/icons/24x24/chevron-right-24.svg";

import imgVoiceBg01 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 01.png";
import imgVoiceBg02 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 02.png";
import imgVoiceBg03 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 03.png";
import imgVoiceBg04 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 04.png";
import imgVoiceBg05 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 05.png";
import imgVoiceBg06 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 06.png";
import imgVoiceBg07 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 07.png";
import imgVoiceBg08 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 08.png";

const VOICE_IMAGES = [
  imgVoiceBg01,
  imgVoiceBg02,
  imgVoiceBg03,
  imgVoiceBg04,
  imgVoiceBg05,
  imgVoiceBg06,
  imgVoiceBg07,
  imgVoiceBg08,
];

/** Disc diameter in px — carousel layout */
const DISC_SIZE = 140;
/** Gap between disc edges in px — carousel layout */
const DISC_GAP = 90;
/** Distance from one disc center to the next — carousel layout */
const STEP = DISC_SIZE + DISC_GAP;
/** Disc diameter in px — fourWide layout */
const FOUR_WIDE_DISC_SIZE = 160;

function clampIndex(index: number, total: number) {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(total - 1, index));
}

function getSlideDirection(currentIndex: number, nextIndex: number): 1 | -1 {
  if (currentIndex === nextIndex) return 1;
  return nextIndex > currentIndex ? 1 : -1;
}

export interface SliderGenerateTTSDemoProps {
  voices: VoiceWithPreview[];
  generateVoices?: VoiceWithPreview[];
  previewLanguage?: string;
  initialText: string;
  mode?: "tabs" | "examplesOnly";
  variant?: "default" | "voicesWithTTS";
  examplesVariant?: "default" | "orbCarousel" | "fourWide";
  showSignUpPopoverOnDownload?: boolean;
}

function UseVoicePill({ voiceId, href, label }: { voiceId: string; href: string; label: string }) {
  return (
    <m.div
      key='use-voice-pill'
      className='tw-absolute -tw-bottom-3 tw-left-1/2 tw-z-10'
      initial={{ opacity: 0, x: "-50%", y: -6 }}
      animate={{ opacity: 1, x: "-50%", y: 0 }}
      exit={{ opacity: 0, x: "-50%", y: -6 }}
      transition={{ duration: 0.2 }}
    >
      <LinkTracked
        asButton
        buttonVariants={{ variant: "elevated", size: "sm" }}
        className='tw-whitespace-nowrap'
        href={href}
        eventName={EventName.mw_tts_voice_library_explore_clicked}
        eventProperties={{ source_component: "slider_generate_tts_omnibox", voice_id: voiceId }}
      >
        {label}
      </LinkTracked>
    </m.div>
  );
}

export function SliderGenerateTTSDemo({
  voices,
  generateVoices,
  previewLanguage,
  initialText,
  mode = "tabs",
  variant = "default",
  examplesVariant = "default",
  showSignUpPopoverOnDownload = false,
}: SliderGenerateTTSDemoProps) {
  return (
    <NativeAudioProvider>
      <SliderGenerateTTSDemoInner
        voices={voices}
        generateVoices={generateVoices}
        previewLanguage={previewLanguage}
        initialText={initialText}
        mode={mode}
        variant={variant}
        examplesVariant={examplesVariant}
        showSignUpPopoverOnDownload={showSignUpPopoverOnDownload}
      />
    </NativeAudioProvider>
  );
}

function SliderGenerateTTSDemoInner({
  voices,
  generateVoices,
  previewLanguage,
  initialText,
  mode = "tabs",
  variant = "default",
  examplesVariant = "default",
  showSignUpPopoverOnDownload = false,
}: SliderGenerateTTSDemoProps) {
  const tTTS = useTranslations("ClientKeys.TTS");
  const tVoicesSlider = useTranslations("ClientKeys.VoicesSlider");
  const { play, togglePlay, playingId, isPaused, stop: stopNativeAudio } = useNativeAudio();
  const voicesWithTTSRef = useRef<VoicesWithTTSHandle>(null);
  const [selectedTab, setSelectedTab] = useState<"examples" | "generate">("examples");
  const [isDownloadSignUpPopoverOpen, setIsDownloadSignUpPopoverOpen] = useState(false);

  const isFourWide = examplesVariant === "fourWide";
  const isOrbCarousel = examplesVariant === "orbCarousel";
  const isExamplesOnly = mode === "examplesOnly";

  // FourWide: native scroll with AutoScrollGrid pattern
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const maxScroll = container.scrollWidth - container.clientWidth;
    setCanScrollPrev(container.scrollLeft > 10);
    setCanScrollNext(container.scrollLeft < maxScroll - 10);
  }, []);

  // Callback ref — re-checks scroll state on mount/remount (AnimatePresence)
  // and observes resize so arrows stay correct when the viewport changes.
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const scrollContainerCallbackRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      scrollContainerRef.current = el;
      if (el) {
        updateScrollState();
        const observer = new ResizeObserver(() => updateScrollState());
        observer.observe(el);
        resizeObserverRef.current = observer;
      }
    },
    [updateScrollState],
  );

  // Reads rendered item width + computed gap so scroll amount adapts to breakpoint
  const getScrollAmount = useCallback(() => {
    const firstItem = firstItemRef.current;
    const container = scrollContainerRef.current;
    if (!firstItem || !container) return FOUR_WIDE_DISC_SIZE;
    const gap = parseFloat(getComputedStyle(container).gap) || 16;
    return firstItem.offsetWidth + gap;
  }, []);

  const layout = useMemo(() => {
    if (isFourWide) {
      return { discSize: FOUR_WIDE_DISC_SIZE, step: 0, maxIndex: 0, setsActiveOnClick: false };
    }
    return { discSize: DISC_SIZE, step: STEP, maxIndex: voices.length - 1, setsActiveOnClick: true };
  }, [isFourWide, voices.length]);

  const [activeIndex, setActiveIndex] = useState(() => (isFourWide ? 0 : Math.floor(voices.length / 2)));
  const [slideDirection, setSlideDirection] = useState<1 | -1>(1);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const hasFinePointer = useHasFinePointer();
  const maxCarouselIndex = Math.max(voices.length - 1, 0);

  useEffect(() => {
    if (isFourWide) return;

    setActiveIndex(prev => clampIndex(prev, voices.length));
  }, [isFourWide, voices.length]);

  const playVoiceAtIndex = useCallback(
    (index: number) => {
      const voice = voices[index];
      if (!voice) return;

      void play({
        id: voice.voiceId,
        url: getPreviewUrlForLanguage(voice, previewLanguage),
        trackingProps: { source: "slider_generate_tts", voice_id: voice.voiceId, position: index },
      });
    },
    [play, previewLanguage, voices],
  );

  // FourWide: scrollTo. Carousel: setActiveIndex.
  const scrollPrev = useCallback(() => {
    if (isFourWide) {
      const container = scrollContainerRef.current;
      if (!container) return;
      const amount = getScrollAmount();
      container.scrollTo({ left: Math.max(0, container.scrollLeft - amount), behavior: "smooth" });
    } else {
      const nextIndex = clampIndex(activeIndex - 1, voices.length);
      if (nextIndex === activeIndex) return;

      if (isOrbCarousel) {
        setSlideDirection(-1);
        if (playingId && !isPaused) {
          playVoiceAtIndex(nextIndex);
        } else {
          stopNativeAudio();
        }
      }

      setActiveIndex(nextIndex);
    }
  }, [
    activeIndex,
    getScrollAmount,
    isFourWide,
    isPaused,
    isOrbCarousel,
    playVoiceAtIndex,
    playingId,
    stopNativeAudio,
    voices.length,
  ]);

  const scrollNext = useCallback(() => {
    if (isFourWide) {
      const container = scrollContainerRef.current;
      if (!container) return;
      const amount = getScrollAmount();
      const maxScroll = container.scrollWidth - container.clientWidth;
      container.scrollTo({ left: Math.min(maxScroll, container.scrollLeft + amount), behavior: "smooth" });
    } else {
      const nextIndex = clampIndex(activeIndex + 1, voices.length);
      if (nextIndex === activeIndex) return;

      if (isOrbCarousel) {
        setSlideDirection(1);
        if (playingId && !isPaused) {
          playVoiceAtIndex(nextIndex);
        } else {
          stopNativeAudio();
        }
      }

      setActiveIndex(nextIndex);
    }
  }, [
    activeIndex,
    getScrollAmount,
    isFourWide,
    isPaused,
    isOrbCarousel,
    playVoiceAtIndex,
    playingId,
    stopNativeAudio,
    voices.length,
  ]);

  const handleDiscToggle = useCallback(
    (voice: VoiceWithPreview, index: number) => {
      togglePlay({
        id: voice.voiceId,
        url: getPreviewUrlForLanguage(voice, previewLanguage),
        trackingProps: { source: "slider_generate_tts", voice_id: voice.voiceId, position: index },
      });
    },
    [previewLanguage, togglePlay],
  );

  const handleSelectVoice = useCallback(
    (index: number) => {
      setActiveIndex(index);
      playVoiceAtIndex(index);
    },
    [playVoiceAtIndex],
  );

  const handleToggleActiveVoice = useCallback(() => {
    const voice = voices[activeIndex];
    if (!voice) return;

    void togglePlay({
      id: voice.voiceId,
      url: getPreviewUrlForLanguage(voice, previewLanguage),
      trackingProps: { source: "slider_generate_tts", voice_id: voice.voiceId, position: activeIndex },
    });
  }, [activeIndex, previewLanguage, togglePlay, voices]);

  const handleSelectOrbIndex = useCallback(
    (index: number) => {
      if (isOrbCarousel) {
        setSlideDirection(getSlideDirection(activeIndex, index));
      }
      handleSelectVoice(index);
    },
    [activeIndex, handleSelectVoice, isOrbCarousel],
  );

  // FourWide: mutates scrollLeft directly (fine-pointer only; touch uses native scroll)
  // Carousel: accumulates dragOffset for the CSS transform, snaps on release
  const { moveProps } = useMove({
    onMoveStart: () => setIsDragging(true),
    onMoveEnd: () => {
      setIsDragging(false);
      if (!isFourWide) {
        const snapped = Math.max(0, Math.min(layout.maxIndex, Math.round(activeIndex - dragOffset / layout.step)));
        setActiveIndex(snapped);
        setDragOffset(0);
      }
    },
    onMove: e => {
      if (e.pointerType === "keyboard") {
        if (e.deltaX > 0) scrollNext();
        else if (e.deltaX < 0) scrollPrev();
        return;
      }
      if (isFourWide) {
        const container = scrollContainerRef.current;
        if (container) container.scrollLeft -= e.deltaX * 1.5;
      } else {
        setDragOffset(prev => {
          const next = prev + e.deltaX * 1.5;
          const min = -(layout.maxIndex - activeIndex) * layout.step;
          const max = activeIndex * layout.step;
          return Math.max(min, Math.min(max, next));
        });
      }
    },
  });

  const canGoPrev = isFourWide ? canScrollPrev : activeIndex > 0;
  const canGoNext = isFourWide ? canScrollNext : activeIndex < maxCarouselIndex;
  const carouselOffset = -(activeIndex * layout.step) - DISC_SIZE / 2 + dragOffset;

  const fourWideContent = (
    <div className='tw-relative tw-w-full'>
      {/* FourWide: native scroll container */}
      <div
        ref={scrollContainerCallbackRef}
        className={clsx(
          "tw-flex tw-items-start tw-gap-4 tw-overflow-x-auto tw-overflow-y-hidden tw-scrollbar-none tw-px-8",
          hasFinePointer && (isDragging ? "tw-cursor-grabbing" : "tw-cursor-grab"),
        )}
        onScroll={updateScrollState}
        {...(hasFinePointer ? moveProps : {})}
      >
        {voices.map((voice, index) => {
          const image = VOICE_IMAGES[index % VOICE_IMAGES.length];
          return (
            <div
              key={voice.voiceId}
              ref={index === 0 ? firstItemRef : null}
              className='tw-flex tw-flex-col tw-items-center tw-gap-8 tw-flex-none tw-w-[calc(100%_-_2rem)] md:tw-w-[calc((100%_-_1rem)/2)] lg:tw-w-[calc((100%_-_2rem)/3)] xl:tw-w-[calc((100%_-_3rem)/4)]'
            >
              <div className='tw-relative' style={{ width: `${layout.discSize}px`, height: `${layout.discSize}px` }}>
                <DiscPlayer
                  image={{
                    src: image,
                    alt: voice.name,
                    blur: 5,
                    sizes: ImageSizes.FULL_1_4_ON_LG_HALF_ON_MD,
                  }}
                  isPlaying={playingId === voice.voiceId}
                  onClick={() => handleDiscToggle(voice, index)}
                />
                <AnimatePresence>
                  {playingId === voice.voiceId && (
                    <UseVoicePill
                      voiceId={voice.voiceId}
                      href={voice.link ?? appUrls.voiceLibrary}
                      label={tVoicesSlider("use_voice")}
                    />
                  )}
                </AnimatePresence>
              </div>
              <div className='tw-text-center tw-px-1' style={{ maxWidth: `${layout.discSize}px` }}>
                <p className='tw-type-base tw-py-1 tw-pb-3 tw-font-medium tw-text-black tw-text-pretty'>
                  {voice.category || voice.name}
                </p>
                {voice.shortDescription && (
                  <p className='tw-type-xs tw-py-1 tw-text-gray-500 tw-text-pretty'>{voice.shortDescription}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Edge fade gradients */}
      <div className='tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-w-16 tw-bg-gradient-to-r tw-from-cream tw-to-transparent' />
      <div className='tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-w-16 tw-bg-gradient-to-l tw-from-cream tw-to-transparent' />

      {/* Inline arrows — fourWide mobile only (hidden at lg) */}
      <div
        className='tw-pointer-events-none tw-absolute -tw-left-3 -tw-right-3 tw-flex tw-items-center tw-justify-between tw-z-10 lg:tw-hidden'
        style={{ top: `${layout.discSize / 2}px`, transform: "translateY(-50%)" }}
      >
        <button
          className={clsx(
            "tw-pointer-events-auto tw-size-11 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-text-black/60 hover:tw-text-black tw-outline-none focus-visible:tw-outline-focus tw-transition-all",
            !canGoPrev && "tw-opacity-0 !tw-pointer-events-none",
          )}
          onClick={scrollPrev}
          disabled={!canGoPrev}
          aria-label={tVoicesSlider("previous_voice")}
        >
          <ChevronLeft24 />
        </button>
        <button
          className={clsx(
            "tw-pointer-events-auto tw-size-11 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-text-black/60 hover:tw-text-black tw-outline-none focus-visible:tw-outline-focus tw-transition-all",
            !canGoNext && "tw-opacity-0 !tw-pointer-events-none",
          )}
          onClick={scrollNext}
          disabled={!canGoNext}
          aria-label={tVoicesSlider("next_voice")}
        >
          <ChevronRight24 />
        </button>
      </div>
    </div>
  );

  const defaultCarouselContent = (
    <div className='tw-relative tw-w-full tw-overflow-hidden'>
      <div
        className={clsx(
          "tw-flex tw-items-start",
          hasFinePointer && (isDragging ? "tw-cursor-grabbing" : "tw-cursor-grab"),
        )}
        style={{
          position: "relative",
          left: "50%",
          width: "max-content",
          gap: `${DISC_GAP}px`,
          touchAction: "none",
          transform: `translateX(${carouselOffset}px)`,
          transition: isDragging ? "none" : "transform 0.3s ease",
        }}
        {...moveProps}
      >
        {voices.map((voice, index) => {
          const image = VOICE_IMAGES[index % VOICE_IMAGES.length];
          return (
            <div
              key={voice.voiceId}
              className='tw-flex tw-flex-col tw-items-center tw-gap-8 tw-flex-none'
              style={{ width: `${layout.discSize}px` }}
            >
              <div className='tw-relative' style={{ width: `${layout.discSize}px`, height: `${layout.discSize}px` }}>
                <DiscPlayer
                  image={{
                    src: image,
                    alt: voice.name,
                    blur: 5,
                    sizes: ImageSizes.FULL_1_4_ON_LG_HALF_ON_MD,
                  }}
                  isPlaying={playingId === voice.voiceId}
                  onClick={() => {
                    setActiveIndex(index);
                    handleDiscToggle(voice, index);
                  }}
                />
              </div>
              <div className='tw-text-center tw-px-1' style={{ maxWidth: `${layout.discSize}px` }}>
                <p className='tw-type-base tw-py-1 tw-pb-3 tw-font-medium tw-text-black tw-text-pretty'>
                  {voice.category || voice.name}
                </p>
                {voice.shortDescription && (
                  <p className='tw-type-xs tw-py-1 tw-text-gray-500 tw-text-pretty'>{voice.shortDescription}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button
        className={clsx(
          "tw-absolute -tw-translate-x-1/2 -tw-translate-y-1/2 tw-z-10 tw-size-11 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-text-black/60 hover:tw-text-black tw-outline-none focus-visible:tw-outline-focus tw-transition-all",
          activeIndex === 0 && "tw-opacity-0 tw-pointer-events-none",
        )}
        style={{
          top: `${layout.discSize / 2}px`,
          left: `calc(50% - ${layout.discSize / 2 + DISC_GAP / 2}px)`,
        }}
        onClick={() => setActiveIndex(prev => Math.max(0, prev - 1))}
        disabled={activeIndex === 0}
        aria-label={tVoicesSlider("previous_voice")}
      >
        <ChevronLeft24 />
      </button>
      <button
        className={clsx(
          "tw-absolute -tw-translate-x-1/2 -tw-translate-y-1/2 tw-z-10 tw-size-11 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-text-black/60 hover:tw-text-black tw-outline-none focus-visible:tw-outline-focus tw-transition-all",
          activeIndex === layout.maxIndex && "tw-opacity-0 tw-pointer-events-none",
        )}
        style={{
          top: `${layout.discSize / 2}px`,
          left: `calc(50% + ${layout.discSize / 2 + DISC_GAP / 2}px)`,
        }}
        onClick={() => setActiveIndex(prev => Math.min(layout.maxIndex, prev + 1))}
        disabled={activeIndex === layout.maxIndex}
        aria-label={tVoicesSlider("next_voice")}
      >
        <ChevronRight24 />
      </button>
    </div>
  );

  const examplesContent = isFourWide ? (
    fourWideContent
  ) : isOrbCarousel ? (
    <ViewLayout
      footer={
        <div className='tw-pt-[3.125rem] md:tw-pt-20'>
          <OrbCarouselFooter
            voices={voices}
            activeIndex={activeIndex}
            slideDirection={slideDirection}
            onPrevious={scrollPrev}
            onNext={scrollNext}
          />
        </div>
      }
    >
      <OrbCarouselContent
        voices={voices}
        activeIndex={activeIndex}
        playingId={playingId}
        isPaused={isPaused}
        onSelectOrbIndex={handleSelectOrbIndex}
        onToggleActiveVoice={handleToggleActiveVoice}
        onPrevious={scrollPrev}
        onNext={scrollNext}
      />
    </ViewLayout>
  ) : (
    defaultCarouselContent
  );

  const tabItems = [
    { id: "examples" as const, title: tTTS("widget.tab_examples") },
    { id: "generate" as const, title: tTTS("widget.tab_generate") },
  ];

  const handleTabChange = useCallback(
    (key: "examples" | "generate") => {
      stopNativeAudio();
      voicesWithTTSRef.current?.stopAudio();
      setSelectedTab(key);
      captureEvent(EventName.mw_tts_voice_slider_switcher, { tab: key });
    },
    [stopNativeAudio],
  );

  const handleGenerateTabDownload = useCallback(() => {
    if (!showSignUpPopoverOnDownload) return;
    if (safeLocalStorage.getItem(LocalStorageKeys.TTS_SIGNUP_POPOVER_DISMISSED)) return;
    captureEvent(EventName.mw_tts_signup_popover_shown);
    setIsDownloadSignUpPopoverOpen(true);
  }, [showSignUpPopoverOnDownload]);

  const tabContentArea = (
    <div className='tw-flex tw-flex-1 tw-flex-col tw-min-h-0 tw-relative'>
      <AnimatePresence initial={false}>
        {selectedTab === "examples" && (
          <m.div
            key='examples'
            className='tw-absolute tw-inset-0 tw-flex tw-flex-col'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <TabPanel
              id='examples'
              className='tw-flex tw-flex-1 tw-items-center tw-min-h-0 tw-overflow-hidden'
              shouldForceMount
            >
              {examplesContent}
            </TabPanel>
          </m.div>
        )}
      </AnimatePresence>
      <AnimatePresence initial={false}>
        {selectedTab === "generate" && (
          <m.div
            key='generate'
            className={clsx(
              "tw-absolute tw-inset-0 tw-flex tw-pt-5",
              isOrbCarousel && variant === "voicesWithTTS"
                ? "tw-flex-col tw-px-4 tw-pb-4 md:tw-px-8 md:tw-pb-8"
                : "tw-items-center tw-justify-center",
              isOrbCarousel && variant !== "voicesWithTTS" && "tw-pb-4 md:tw-pb-8",
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <TabPanel
              id='generate'
              className={clsx(
                "tw-w-full",
                isOrbCarousel && variant === "voicesWithTTS"
                  ? "tw-flex-1 tw-min-h-0 [&>div]:!tw-h-full"
                  : variant === "voicesWithTTS" && "tw-h-full",
                isOrbCarousel && variant !== "voicesWithTTS" && "tw-p-4 md:tw-p-8",
              )}
              shouldForceMount
            >
              {variant === "voicesWithTTS" ? (
                <VoicesWithTTS
                  ref={voicesWithTTSRef}
                  voices={generateVoices ?? voices}
                  defaultLanguage={previewLanguage}
                  displayCategory
                  initialPrompt={{ text: initialText }}
                  initialSelectedVoiceId={(generateVoices ?? voices)[activeIndex]?.voiceId}
                  downloadButton='show'
                  showBorder
                  mobileEditorOnly
                  onAfterDownload={handleGenerateTabDownload}
                  onBackOverride={() => setSelectedTab("examples")}
                />
              ) : (
                <SpeechInner
                  initialVoices={generateVoices ?? voices}
                  initialLanguage={previewLanguage ?? "en"}
                  initialText={initialText}
                  initialSelectedVoiceId={(generateVoices ?? voices)[activeIndex]?.voiceId}
                  variant='mini'
                  size='lg'
                  showDownloadButton
                  onAfterDownload={handleGenerateTabDownload}
                />
              )}
            </TabPanel>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );

  const omniboxContent = isOrbCarousel ? (
    isExamplesOnly ? (
      <OmniboxLayoutWrapper className='tw-h-[28.75rem] md:tw-h-[36rem]' padding='tw-px-0'>
        <div className='tw-flex tw-flex-col tw-size-full'>
          <div className='tw-flex tw-flex-1 tw-flex-col tw-min-h-0 tw-relative'>
            <div aria-hidden='true' className='tw-flex tw-items-start tw-justify-between tw-px-8 tw-pt-8 tw-pb-0'>
              <div className='tw-flex-1 tw-min-w-0' />
              <div className='tw-ml-4 tw-flex-1 tw-min-w-0 tw-flex tw-justify-end'>
                <div className='tw-h-0 md:tw-h-11 tw-w-[11.8125rem] tw-opacity-0' />
              </div>
            </div>
            <div className='tw-flex tw-flex-1 tw-items-center tw-min-h-0 tw-overflow-hidden'>{examplesContent}</div>
          </div>
        </div>
      </OmniboxLayoutWrapper>
    ) : (
      <Tabs
        className='tw-relative tw-isolate'
        selectedKey={selectedTab}
        onSelectionChange={key => handleTabChange(key as "examples" | "generate")}
      >
        {/* Hide section side lines behind mobile TabList */}
        <div
          className='tw-absolute -tw-z-10 tw-bottom-full tw-h-16 -tw-inset-x-px md:tw-hidden'
          style={{ backgroundColor: "var(--marker-background-color)" }}
        />
        {/* Mobile TabList — positioned above the card */}
        <div className='tw-absolute tw-bottom-full tw-mb-2 tw-left-0 tw-w-full tw-z-50 md:tw-hidden'>
          <TabList size='lg' surface='cream' items={tabItems} background fullWidth equalWidth />
        </div>

        <OmniboxLayoutWrapper className='tw-h-[28.75rem] md:tw-h-[36rem]' padding='tw-px-0'>
          <div className='tw-flex tw-flex-col tw-size-full'>
            <div
              className={clsx(
                "tw-flex-1 tw-min-h-0 tw-flex tw-flex-col",
                selectedTab === "examples" && "tw-pt-14 md:tw-pt-0",
              )}
            >
              {/* Desktop-only TabList header inside card */}
              <div className='tw-hidden md:tw-flex tw-items-start tw-justify-between tw-p-8 tw-pb-0'>
                <div className='tw-flex-1 tw-min-w-0' />
                <div className='tw-ml-4 tw-flex-1 tw-min-w-0 tw-flex tw-justify-end'>
                  <TabList size='lg' surface='cream' items={tabItems} />
                </div>
              </div>

              {tabContentArea}
            </div>
          </div>
        </OmniboxLayoutWrapper>
      </Tabs>
    )
  ) : (
    <OmniboxLayoutWrapper className='tw-h-[28.75rem] md:tw-h-[36rem]'>
      <div className='tw-flex tw-flex-col tw-size-full'>
        {isExamplesOnly ? (
          <div className='tw-flex tw-flex-1 tw-flex-col tw-min-h-0 tw-relative'>
            <div
              aria-hidden='true'
              className='tw-flex tw-items-center md:tw-items-start md:tw-justify-between tw-justify-center'
            >
              <div className='tw-flex-1 tw-min-w-0 tw-hidden md:tw-block' />
              <div className='md:tw-ml-4 md:tw-flex-1 tw-min-w-0 md:tw-flex md:tw-justify-end'>
                <div className='tw-h-11 tw-w-[11.8125rem] tw-opacity-0' />
              </div>
            </div>
            <div className='tw-flex tw-flex-1 tw-items-center tw-min-h-0 tw-overflow-hidden'>{examplesContent}</div>
          </div>
        ) : (
          <Tabs
            className='tw-flex-1 tw-min-h-0 tw-flex tw-flex-col'
            selectedKey={selectedTab}
            onSelectionChange={key => handleTabChange(key as "examples" | "generate")}
          >
            <div className='tw-flex tw-items-center md:tw-items-start md:tw-justify-between tw-justify-center'>
              <div className='tw-flex-1 tw-min-w-0 tw-hidden md:tw-block' />
              <div className='md:tw-ml-4 md:tw-flex-1 tw-min-w-0 md:tw-flex md:tw-justify-end'>
                <TabList size='lg' surface='cream' items={tabItems} />
              </div>
            </div>
            {tabContentArea}
          </Tabs>
        )}
        <div className='tw-flex tw-justify-center'>
          <LinkTracked
            asButton
            buttonVariants={{ variant: "elevated", size: "lg" }}
            href={appUrls.voiceLibrary}
            eventName={EventName.mw_tts_voice_library_explore_clicked}
            eventProperties={{ source_component: "slider_generate_tts_omnibox" }}
          >
            {tTTS("widget.explore_all_voices")}
          </LinkTracked>
        </div>
      </div>
    </OmniboxLayoutWrapper>
  );

  if (isFourWide) {
    return (
      <>
        <div className='tw-relative'>
          {omniboxContent}
          {/* External navigation arrows — desktop only, matches AutoScrollGrid style */}
          {(isExamplesOnly || selectedTab === "examples") && (
            <div className='tw-pointer-events-none tw-absolute tw-inset-y-0 -tw-left-[calc(2.75rem+1rem+var(--page-section-px,0px))] -tw-right-[calc(2.75rem+1rem+var(--page-section-px,0px))] tw-hidden tw-items-center tw-justify-between lg:tw-flex'>
              <div className='tw-pointer-events-auto'>
                <Button
                  aria-label={tVoicesSlider("previous_voice")}
                  className={clsx(
                    "tw-flex tw-size-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-p-0 tw-transition-opacity tw-duration-200",
                    !canGoPrev && "tw-opacity-30 tw-cursor-not-allowed",
                  )}
                  disabled={!canGoPrev}
                  icon={<ChevronLeft24 />}
                  onClick={scrollPrev}
                  variant='elevated'
                />
              </div>
              <div className='tw-pointer-events-auto'>
                <Button
                  aria-label={tVoicesSlider("next_voice")}
                  className={clsx(
                    "tw-flex tw-size-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-p-0 tw-transition-opacity tw-duration-200",
                    !canGoNext && "tw-opacity-30 tw-cursor-not-allowed",
                  )}
                  disabled={!canGoNext}
                  icon={<ChevronRight24 />}
                  onClick={scrollNext}
                  variant='elevated'
                />
              </div>
            </div>
          )}
        </div>
        <TTSDownloadSignUpPopover
          isOpen={isDownloadSignUpPopoverOpen}
          onOpenChange={open => {
            setIsDownloadSignUpPopoverOpen(open);
            if (!open) {
              captureEvent(EventName.mw_tts_signup_popover_closed);
              safeLocalStorage.setItem(LocalStorageKeys.TTS_SIGNUP_POPOVER_DISMISSED, "1");
            }
          }}
        />
      </>
    );
  }

  if (isOrbCarousel) {
    return (
      <>
        <div className={clsx(!isExamplesOnly && "tw-mt-14 md:tw-mt-0")}>{omniboxContent}</div>
        <TTSDownloadSignUpPopover
          isOpen={isDownloadSignUpPopoverOpen}
          onOpenChange={open => {
            setIsDownloadSignUpPopoverOpen(open);
            if (!open) {
              captureEvent(EventName.mw_tts_signup_popover_closed);
              safeLocalStorage.setItem(LocalStorageKeys.TTS_SIGNUP_POPOVER_DISMISSED, "1");
            }
          }}
        />
        <div className='tw-flex tw-justify-center tw-pt-4'>
          <LinkTracked
            asButton
            buttonVariants={{ variant: "ghost", size: "lg" }}
            href={appUrls.voiceLibrary}
            eventName={EventName.mw_tts_voice_library_explore_clicked}
            eventProperties={{ source_component: "orb_carousel_cta_below" }}
            className='tw-gap-1.5'
          >
            {tVoicesSlider("explore_voice_library")}
            <IconArrowUpRight className={clsx("tw-size-4", iconStrokeFix)} />
          </LinkTracked>
        </div>
      </>
    );
  }

  return (
    <>
      {omniboxContent}
      <TTSDownloadSignUpPopover
        isOpen={isDownloadSignUpPopoverOpen}
        onOpenChange={open => {
          setIsDownloadSignUpPopoverOpen(open);
          if (!open) {
            captureEvent(EventName.mw_tts_signup_popover_closed);
            safeLocalStorage.setItem(LocalStorageKeys.TTS_SIGNUP_POPOVER_DISMISSED, "1");
          }
        }}
      />
    </>
  );
}
